import React, { useState, useEffect, useCallback } from 'react';
// Try with explicit imports from specific modules
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Nav from 'react-bootstrap/Nav';
import { useDispatch, useSelector } from 'react-redux';
import { createMeeting, meetingSlice, updateMeeting } from '../../features/meeting/meetingSlice';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { toast } from 'react-hot-toast';
import meetingService from '../../features/meeting/meetingService';
import bookingFunctionsService from '../../features/booking/bookingFunctionsService';

const isGoogleConnectionEnabled =
  process.env.REACT_APP_ENABLE_GOOGLE_CONNECTION === 'true';
const isZoomConnectionEnabled =
  process.env.REACT_APP_ENABLE_ZOOM_CONNECTION === 'true';

const pad2 = (n) => String(n).padStart(2, '0');

const getDefaultDateTimes = () => {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setMinutes(0, 0, 0);
    nextHour.setHours(nextHour.getHours() + 1);

    const end = new Date(nextHour.getTime() + 30 * 60 * 1000);

    return {
        date: `${nextHour.getFullYear()}-${pad2(nextHour.getMonth() + 1)}-${pad2(nextHour.getDate())}`,
        startTime: `${pad2(nextHour.getHours())}:${pad2(nextHour.getMinutes())}`,
        endTime: `${pad2(end.getHours())}:${pad2(end.getMinutes())}`,
    };
};

const MEETING_TYPE_OPTIONS = [
    { value: '0', label: 'Fiziksel' },
    { value: '1', label: 'Online' },
    { value: '2', label: 'Hibrit' },
    { value: '3', label: 'Telefon' },
    { value: '4', label: 'Yazışma' },
    { value: '99', label: 'Diğer' },
];

const MEETING_KIND_OPTIONS = [
    { value: 'LION', label: 'LION' },
    { value: 'BIREBIR', label: 'BİREBİR' },
    { value: 'DIGER', label: 'DİĞER' },
    { value: 'STANDART', label: 'STANDART' },
];

const meetingTemplateToKind = (meetingTemplate) => {
    const template = String(meetingTemplate ?? '0');
    if (template === '2') return 'LION';
    if (template === '1') return 'BIREBIR';
    if (template === '99') return 'DIGER';
    return 'STANDART';
};

const toSafeDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (typeof value?.toDate === 'function') return value.toDate();
    if (typeof value === 'number') return new Date(value);
    if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
};

const splitDateTime = (value) => {
    const d = toSafeDate(value);
    if (!d) return { date: '', time: '' };
    return {
        date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
        time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
    };
};

function MeetingEventCreator() {
    const [searchParams] = useSearchParams();
    const editMeetingId = searchParams.get('editMeetingId');
    const isEditMode = Boolean(editMeetingId);
    const defaultDateTimes = getDefaultDateTimes();
    const [activeTab, setActiveTab] = useState('meeting');
    
    // Meeting form data
    const [meetingFormData, setMeetingFormData] = useState({
        title: '',
        date: defaultDateTimes.date,
        startTime: defaultDateTimes.startTime,
        endTime: defaultDateTimes.endTime,
        briefing: '',
        meetingType: '',
        meetingKind: '',
        physicalLocation: '',
        physicalMapLink: '',
        onlineMeetingLink: ''
    });
    
    // Enhanced Event form data with mobile features
    const [eventFormData, setEventFormData] = useState({
        title: '',
        startDate: defaultDateTimes.date,
        endDate: defaultDateTimes.date,
        startTime: defaultDateTimes.startTime,
        endTime: defaultDateTimes.endTime,
        description: '',
        location: '',
        capacity: '',
        amount: '',
        coverPhoto: null,
        documents: [],
        eventScope: '', // kurumsal/bireysel
        eventType: '',  // fiziksel/online
        mapLocation: '',
        onlineUrl: '',
        // New mobile features
        quota: '', // Kontenjan (Sınırsız/Sınırlı)
        hasWebsite: 'no', // Web Sitesi Var/Yok
        participantApproval: 'no', // Katılımcı Onayı (Evet/Hayır)
        eventCategory: '', // Expo, Congress, Conference, etc.
        suggestion: 'no', // Öne Çıkar (Hayır/Evet)
        addDescription: '', // Etkinlik ile ilgili açıklama ekleyin
        businessId: ''

    });
    
    const [adminBusinesses, setAdminBusinesses] = useState([]);
    const [meetingLinkSource, setMeetingLinkSource] = useState('manual');
    const [eventLinkSource, setEventLinkSource] = useState('manual');
    const [googleConnection, setGoogleConnection] = useState({
        providerConnected: false,
        providerEmail: '',
    });
    const [zoomConnection, setZoomConnection] = useState({
        providerConnected: false,
        providerEmail: '',
    });
    const [isCheckingGoogle, setIsCheckingGoogle] = useState(false);
    const [isCheckingZoom, setIsCheckingZoom] = useState(false);
    const [isGeneratingMeetLink, setIsGeneratingMeetLink] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const { loadingStates, errors } = useSelector(
        (state) => state.meeting
    );
    const userId = useSelector((state) => state.auth.user?.uid);
    const isLoading = isEditMode ? loadingStates?.updateMeeting : loadingStates?.createMeeting;
    const createError = errors?.createMeeting;
    const updateError = errors?.updateMeeting;

    useEffect(() => {
        let isMounted = true;
        const loadMeetingForEdit = async () => {
            if (!editMeetingId) return;
            try {
                const meeting = await meetingService.getMeeting(editMeetingId);
                if (!isMounted || !meeting) return;

                const start = splitDateTime(meeting.startDate);
                const end = splitDateTime(meeting.endDate);
                const meetingLink =
                    meeting.urlAttendee ||
                    meeting.urlHost ||
                    meeting.url ||
                    '';

                setActiveTab('meeting');
                setMeetingFormData((prev) => ({
                    ...prev,
                    title: meeting.title || '',
                    date: start.date || prev.date,
                    startTime: start.time || prev.startTime,
                    endTime: end.time || prev.endTime,
                    briefing: meeting.meetingDescription || '',
                    meetingType: String(meeting.type ?? ''),
                    meetingKind: meetingTemplateToKind(meeting.meetingTemplate),
                    physicalLocation: meeting.location || '',
                    physicalMapLink: meeting.urlLocation || '',
                    onlineMeetingLink: meetingLink,
                }));
                setMeetingLinkSource('manual');
            } catch (error) {
                if (isMounted) {
                    toast.error(error?.message || 'Toplantı bilgileri yüklenemedi.');
                }
            }
        };

        loadMeetingForEdit();
        return () => {
            isMounted = false;
        };
    }, [editMeetingId]);

    const loadGoogleConnection = useCallback(async () => {
        if (!isGoogleConnectionEnabled) {
            setGoogleConnection({
                providerConnected: false,
                providerEmail: '',
            });
            return;
        }
        if (!userId) return;
        setIsCheckingGoogle(true);
        try {
            const status = await bookingFunctionsService.getGoogleConnectionStatus();
            setGoogleConnection({
                providerConnected: status?.providerConnected === true,
                providerEmail: status?.providerEmail || '',
            });
        } catch (error) {
            setGoogleConnection({
                providerConnected: false,
                providerEmail: '',
            });
        } finally {
            setIsCheckingGoogle(false);
        }
    }, [userId]);

    const loadZoomConnection = useCallback(async () => {
        if (!isZoomConnectionEnabled) {
            setZoomConnection({
                providerConnected: false,
                providerEmail: '',
            });
            return;
        }
        if (!userId) return;
        setIsCheckingZoom(true);
        try {
            const status = await bookingFunctionsService.getZoomConnectionStatus();
            setZoomConnection({
                providerConnected: status?.providerConnected === true,
                providerEmail: status?.providerEmail || '',
            });
        } catch (error) {
            setZoomConnection({
                providerConnected: false,
                providerEmail: '',
            });
        } finally {
            setIsCheckingZoom(false);
        }
    }, [userId]);

    useEffect(() => {
        loadGoogleConnection();
        loadZoomConnection();
    }, [loadGoogleConnection, loadZoomConnection]);

    useEffect(() => {
        const searchParams = new URLSearchParams(window.location.search);
        const connectedProvider = searchParams.get('connected');
        if (connectedProvider === '1' || connectedProvider === 'google' || connectedProvider === 'zoom') {
            // OAuth dönüşünden sonra UI hemen "bağlı" görünsün, ardından
            // gerçek bağlantı durumu Firestore'dan birkaç kez yeniden doğrulansın.
            if (connectedProvider === 'zoom') {
                setZoomConnection((prev) => ({ ...prev, providerConnected: true }));
                setMeetingLinkSource('zoom');
                setEventLinkSource('zoom');
            } else {
                setGoogleConnection((prev) => ({ ...prev, providerConnected: true }));
                setMeetingLinkSource('google');
                setEventLinkSource('google');
            }

            let attempts = 0;
            const interval = setInterval(() => {
                attempts += 1;
                loadGoogleConnection();
                loadZoomConnection();
                if (attempts >= 6) {
                    clearInterval(interval);
                    window.history.replaceState({}, '', '/MeetingAdd');
                }
            }, 1200);

            return () => clearInterval(interval);
        }
        return undefined;
    }, [loadGoogleConnection, loadZoomConnection]);

    useEffect(() => {
        return () => {
            dispatch(meetingSlice.actions.reset());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!googleConnection.providerConnected && meetingLinkSource === 'google') {
            setMeetingLinkSource('manual');
        }
        if (!zoomConnection.providerConnected && meetingLinkSource === 'zoom') {
            setMeetingLinkSource('manual');
        }
        if (!googleConnection.providerConnected && eventLinkSource === 'google') {
            setEventLinkSource('manual');
        }
        if (!zoomConnection.providerConnected && eventLinkSource === 'zoom') {
            setEventLinkSource('manual');
        }
    }, [googleConnection.providerConnected, zoomConnection.providerConnected, meetingLinkSource, eventLinkSource]);

    const handleMeetingChange = (e) => {
        const { name, value } = e.target;
        setMeetingFormData({
            ...meetingFormData,
            [name]: value
        });
    };

    const handleConnectGoogle = async () => {
        if (!isGoogleConnectionEnabled) {
            setSubmitError('Google bağlantısı env ayarı ile kapalı.');
            return;
        }
        const redirectUrl = `${window.location.origin}/MeetingAdd?connected=google`;
        const response = await bookingFunctionsService.startGoogleOAuth({ redirectUrl });
        if (response?.authUrl) {
            window.location.href = response.authUrl;
        }
    };

    const handleConnectZoom = async () => {
        if (!isZoomConnectionEnabled) {
            setSubmitError('Zoom bağlantısı env ayarı ile kapalı.');
            return;
        }
        const redirectUrl = `${window.location.origin}/MeetingAdd?connected=zoom`;
        const response = await bookingFunctionsService.startZoomOAuth({ redirectUrl });
        if (response?.authUrl) {
            window.location.href = response.authUrl;
        }
    };
    

    const handleEventChange = (e) => {
        const { name, value, type, checked, files } = e.target;
        
        if (type === 'file' && name === 'documents') {
            // Handle multiple document uploads
            const fileArray = Array.from(files);
            setEventFormData({
                ...eventFormData,
                documents: [...eventFormData.documents, ...fileArray]
            });
        } else {
            setEventFormData({
                ...eventFormData,
                [name]: type === 'checkbox' ? checked : value
            });
        }
    };

    // Handle cover photo upload for events
    const handleCoverPhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setEventFormData({
                ...eventFormData,
                coverPhoto: file
            });
        }
    };

    // Remove document from list
    const removeDocument = (index) => {
        const updatedDocs = eventFormData.documents.filter((_, i) => i !== index);
        setEventFormData({
            ...eventFormData,
            documents: updatedDocs
        });
    };

   // Haritalama tablosu — seçime göre Firestore alanları


// ŞABLON HARİTASI (ortak)
const KIND_MAP = {
  LION:     { category: 'Meeting', meetingType: '99', meetingTemplate: '2',  tagTitle: 'LION' },
  BIREBIR:  { category: '0',       meetingType: '0',  meetingTemplate: '1',  tagTitle: 'BİREBİR' },
  DIGER:    { category: '0',       meetingType: '0',  meetingTemplate: '99', tagTitle: 'DİĞER' },
  STANDART: { category: '0',       meetingType: '0',  meetingTemplate: '0',  tagTitle: 'STANDART' },
};

// -----------------------------
// 1) TOPLANTI EKLEME (meeting)
// -----------------------------
const handleMeetingSubmit = async (e) => {
  e.preventDefault();
  setSubmitError('');

  if (!meetingFormData.meetingType) {
    setSubmitError('Lütfen toplantı tipini seçin.');
    return;
  }

  if (!meetingFormData.meetingKind) {
    setSubmitError('Lütfen toplantı tipi (şablon) seçin.');
    return;
  }

  const startDateTime = new Date(meetingFormData.date + 'T' + meetingFormData.startTime);
  const endDateTime   = new Date(meetingFormData.date + 'T' + meetingFormData.endTime);
  const isOnlineOrHybrid = meetingFormData.meetingType === '1' || meetingFormData.meetingType === '2';

  if (!(startDateTime instanceof Date) || Number.isNaN(startDateTime.getTime())) {
    setSubmitError('Başlangıç tarihi/saati geçersiz.');
    return;
  }

  if (!(endDateTime instanceof Date) || Number.isNaN(endDateTime.getTime())) {
    setSubmitError('Bitiş tarihi/saati geçersiz.');
    return;
  }

  if (endDateTime <= startDateTime) {
    setSubmitError('Bitiş saati başlangıç saatinden sonra olmalı.');
    return;
  }

  let onlineMeetingLink = (meetingFormData.onlineMeetingLink || '').trim();

  if (isOnlineOrHybrid && meetingLinkSource === 'google') {
    if (!googleConnection.providerConnected) {
      setSubmitError('Google hesabı bağlı değil. Önce Google hesabını bağlayın.');
      return;
    }

    try {
      setIsGeneratingMeetLink(true);
      const meetData = await meetingService.createGoogleMeetLink({
        startAt: startDateTime.toISOString(),
        endAt: endDateTime.toISOString(),
        title: meetingFormData.title || 'Beetinq görüşmesi',
        description: meetingFormData.briefing || '',
      });
      onlineMeetingLink = meetData?.meetingUrl || '';

      if (!onlineMeetingLink) {
        setSubmitError('Google Meet linki oluşturulamadı.');
        return;
      }
    } catch (error) {
      setSubmitError(error?.message || 'Google Meet linki oluşturulurken hata oluştu.');
      return;
    } finally {
      setIsGeneratingMeetLink(false);
    }
  }

  if (isOnlineOrHybrid && meetingLinkSource === 'zoom') {
    if (!zoomConnection.providerConnected) {
      setSubmitError('Zoom hesabı bağlı değil. Önce Zoom hesabını bağlayın.');
      return;
    }

    try {
      setIsGeneratingMeetLink(true);
      const zoomData = await meetingService.createZoomMeetingLink({
        startAt: startDateTime.toISOString(),
        endAt: endDateTime.toISOString(),
        title: meetingFormData.title || 'Beetinq görüşmesi',
        description: meetingFormData.briefing || '',
      });
      onlineMeetingLink = zoomData?.meetingUrl || '';

      if (!onlineMeetingLink) {
        setSubmitError('Zoom linki oluşturulamadı.');
        return;
      }
    } catch (error) {
      setSubmitError(error?.message || 'Zoom linki oluşturulurken hata oluştu.');
      return;
    } finally {
      setIsGeneratingMeetLink(false);
    }
  }

  if (isOnlineOrHybrid && meetingLinkSource === 'manual' && !onlineMeetingLink) {
    setSubmitError('Online toplantı için bağlantı girin, Google Meet veya Zoom seçin.');
    return;
  }

  // seçilen toplantı şablonu (formda meetingKind olmalı)
  const tpl = KIND_MAP[meetingFormData.meetingKind] || KIND_MAP.STANDART;

  const meetingData = {
    title: meetingFormData.title,
    meetingDescription: meetingFormData.briefing,

    // Fiziksel / Online / Hibrit seçimin (mevcut alanın)
    type: meetingFormData.meetingType,

    location: meetingFormData.physicalLocation || '',
    urlLocation: meetingFormData.physicalMapLink || '',
    urlAttendee: onlineMeetingLink,
    urlHost: onlineMeetingLink,
    url: onlineMeetingLink,

    startDate: Timestamp.fromDate(startDateTime),
    endDate:   Timestamp.fromDate(endDateTime),
    createDate: Timestamp.fromDate(new Date()),
    isactivity: false, // Meetings have isactivity: false

    // ŞABLON ALANLARI
    category: tpl.category,
    meetingType: tpl.meetingType,
    meetingTemplate: tpl.meetingTemplate,
    tags: [{ title: tpl.tagTitle, type: '0', url: '' }],
  };

  // Debug
  console.log('MEETING SEND =>', meetingData);

  try {
    if (isEditMode) {
      await dispatch(updateMeeting({ id: editMeetingId, meetingData })).unwrap();
      toast.success('Toplantı başarıyla güncellendi.');
      navigate(`/MeetingDetail/${editMeetingId}`);
      return;
    }

    await dispatch(createMeeting(meetingData)).unwrap();
    toast.success('Toplantı başarıyla kaydedildi. 3 saniye içinde toplantılar sayfasına yönlendiriliyorsunuz.');
    setTimeout(() => {
      navigate('/dashboard');
    }, 3000);
  } catch (error) {
    const errorText =
      (typeof error === 'string' && error) ||
      error?.message ||
      updateError ||
      createError ||
      (isEditMode ? 'Toplantı güncellenemedi.' : 'Toplantı kaydedilemedi.');
    setSubmitError(errorText);
    toast.error(`${isEditMode ? 'Toplantı güncellenemedi' : 'Toplantı kaydedilemedi'}: ${errorText}`);
  }
};

// ---------------------------
// 2) ETKİNLİK EKLEME (event)
// ---------------------------
const handleEventSubmit = async (e) => {
  e.preventDefault();
  setSubmitError('');

  const startDateTime = new Date(eventFormData.startDate + 'T' + eventFormData.startTime);
  const endDateTime   = new Date(eventFormData.endDate + 'T' + eventFormData.endTime);
  const isOnlineEvent = eventFormData.eventType === 'online';

  if (!(startDateTime instanceof Date) || Number.isNaN(startDateTime.getTime())) {
    setSubmitError('Başlangıç tarihi/saati geçersiz.');
    return;
  }

  if (!(endDateTime instanceof Date) || Number.isNaN(endDateTime.getTime())) {
    setSubmitError('Bitiş tarihi/saati geçersiz.');
    return;
  }

  if (endDateTime <= startDateTime) {
    setSubmitError('Bitiş saati başlangıç saatinden sonra olmalı.');
    return;
  }

  let onlineEventLink = (eventFormData.onlineUrl || '').trim();

  if (isOnlineEvent && eventLinkSource === 'google') {
    if (!googleConnection.providerConnected) {
      setSubmitError('Google hesabı bağlı değil. Önce Google hesabını bağlayın.');
      return;
    }

    try {
      setIsGeneratingMeetLink(true);
      const meetData = await meetingService.createGoogleMeetLink({
        startAt: startDateTime.toISOString(),
        endAt: endDateTime.toISOString(),
        title: eventFormData.title || 'Beetinq etkinliği',
        description: eventFormData.description || '',
      });
      onlineEventLink = meetData?.meetingUrl || '';

      if (!onlineEventLink) {
        setSubmitError('Google Meet linki oluşturulamadı.');
        return;
      }
    } catch (error) {
      setSubmitError(error?.message || 'Google Meet linki oluşturulurken hata oluştu.');
      return;
    } finally {
      setIsGeneratingMeetLink(false);
    }
  }

  if (isOnlineEvent && eventLinkSource === 'zoom') {
    if (!zoomConnection.providerConnected) {
      setSubmitError('Zoom hesabı bağlı değil. Önce Zoom hesabını bağlayın.');
      return;
    }

    try {
      setIsGeneratingMeetLink(true);
      const zoomData = await meetingService.createZoomMeetingLink({
        startAt: startDateTime.toISOString(),
        endAt: endDateTime.toISOString(),
        title: eventFormData.title || 'Beetinq etkinliği',
        description: eventFormData.description || '',
      });
      onlineEventLink = zoomData?.meetingUrl || '';

      if (!onlineEventLink) {
        setSubmitError('Zoom linki oluşturulamadı.');
        return;
      }
    } catch (error) {
      setSubmitError(error?.message || 'Zoom linki oluşturulurken hata oluştu.');
      return;
    } finally {
      setIsGeneratingMeetLink(false);
    }
  }

  if (isOnlineEvent && eventLinkSource === 'manual' && !onlineEventLink) {
    setSubmitError('Online etkinlik için bağlantı girin, Google Meet veya Zoom seçin.');
    return;
  }

  // Etkinlik için de aynı şablonu kullanmak istersen:
  // formda eventFormData.meetingKind yoksa STANDART'a düşer.
  const tplE = KIND_MAP[eventFormData.meetingKind] || KIND_MAP.STANDART;

  const activityData = {
    title: eventFormData.title,
    meetingDescription: eventFormData.description,
    type: "activity",

    location: eventFormData.location || '',
    urlLocation: eventFormData.eventType === 'fiziksel' ? eventFormData.mapLocation : '',
    url: eventFormData.eventType === 'online' ? onlineEventLink : '',
    urlAttendee: eventFormData.eventType === 'online' ? onlineEventLink : '',
    urlHost: eventFormData.eventType === 'online' ? onlineEventLink : '',

    startDate: Timestamp.fromDate(startDateTime),
    endDate:   Timestamp.fromDate(endDateTime),
    createDate: Timestamp.fromDate(new Date()),

    isactivity: true,
    isSubMeeting: false,
    isSubActivity: false,

    // Etkinlik kendi kategorisini kullanıyorsa ona öncelik ver;
    // değilse şablondan gelen category'yi yaz.
    category: eventFormData.eventCategory || tplE.category,

    image: '',
    meetingBusinessState: false,
    premium: true,

    // NOT: Aşağıda businessId iki kez set edilmesin diye sadece en alttaki tek versiyonu kaldı.
    // businessId: [],  // <- BUNU KALDIRDIK

    explore: eventFormData.suggestion === 'yes',
    state: true,
    private: eventFormData.participantApproval === 'yes',
    status: "0",

    blackList: [],
    adminUsers: [],
    users: [],
    follower: [],
    userReq: [],
    nonUserInvited: [],
    invitations: [],
    files: eventFormData.documents,

    tags: [{ title: tplE.tagTitle, type: '0', url: '' }],

    subMeetings: [],
    topMeetingId: "",
    isBtoB: false,
    btobMeetings: [],
    moderatorUsers: [],
    subActivitys: [],
    isBtoBSession: false,
    sessionCount: 0,
    sessionDuration: 0,
    sessionBreakDuaration: 0,
    sessionDeskCount: 0,
    isMatching: false,
    matchingSurveyUsers: [],

    eventScope: eventFormData.eventScope,
    eventType: eventFormData.eventType,
    coverPhoto: eventFormData.coverPhoto,

    isFree: eventFormData.amount === "" || eventFormData.amount === "0",
    amount: eventFormData.amount !== "" ? parseFloat(eventFormData.amount) : 0.0,
    userLimit: eventFormData.capacity !== "" ? parseInt(eventFormData.capacity) : 0,

    urlWeb: '',
    modules: [],
    isFront: false,
    activityOrder: 0,
    activityType: "standard",

    // ŞABLON ALANLARI (etkinliğe de uygulansın istiyorsan)
    meetingType: tplE.meetingType,
    meetingTemplate: tplE.meetingTemplate,

    stableUsers: [],
    changingUsers: [],

    // New fields from mobile
    hasWebsite: eventFormData.hasWebsite === 'yes',
    quota: eventFormData.quota,
    additionalDescription: eventFormData.addDescription,

    // Tek bir yerde set edelim:
    businessId: eventFormData.businessId ? [eventFormData.businessId] : []
  };

  // Debug
  console.log('EVENT SEND =>', activityData);

  try {
    await dispatch(createMeeting(activityData)).unwrap();
    toast.success('Etkinlik başarıyla kaydedildi. 3 saniye içinde etkinlik listesine yönlendiriliyorsunuz.');
    setTimeout(() => {
      navigate('/EventList');
    }, 3000);
  } catch (error) {
    const errorText =
      (typeof error === 'string' && error) ||
      error?.message ||
      createError ||
      'Etkinlik kaydedilemedi.';
    setSubmitError(errorText);
    toast.error(`Etkinlik kaydedilemedi: ${errorText}`);
  }
};

    // Meeting type conditional fields logic
    const showPhysicalFields = meetingFormData.meetingType === '0' || meetingFormData.meetingType === '2';
    const showOnlineFields = meetingFormData.meetingType === '1' || meetingFormData.meetingType === '2';

    const renderMeetingForm = () => (
        <Form onSubmit={handleMeetingSubmit} className="meeting-creator-form">
            <Form.Group className="mb-3">
                <Form.Label>Toplantı Başlığı</Form.Label>
                <Form.Control 
                    type="text" 
                    name="title"
                    value={meetingFormData.title}
                    onChange={handleMeetingChange}
                    placeholder="Başlık girin" 
                    required
                />
            </Form.Group>

            <Row>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Tarih</Form.Label>
                        <Form.Control 
                            type="date" 
                            name="date"
                            value={meetingFormData.date}
                            onChange={handleMeetingChange}
                            required
                        />
                    </Form.Group>
                </Col>
                
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Başlangıç Saati</Form.Label>
                        <Form.Control 
                            type="time" 
                            name="startTime"
                            step={300}
                            value={meetingFormData.startTime}
                            onChange={handleMeetingChange}
                            required
                        />
                    </Form.Group>
                </Col>
                
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>Bitiş Saati</Form.Label>
                        <Form.Control 
                            type="time" 
                            name="endTime"
                            step={300}
                            value={meetingFormData.endTime}
                            onChange={handleMeetingChange}
                            required
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Toplantı Hakkında Brifing</Form.Label>
                <Form.Control 
                    as="textarea" 
                    rows={3}
                    name="briefing"
                    value={meetingFormData.briefing}
                    onChange={handleMeetingChange}
                    placeholder="Toplantının amacı, gündemi veya diğer bilgileri..." 
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Toplantı Tipi</Form.Label>
                <div className="meeting-choice-grid">
                    {MEETING_TYPE_OPTIONS.map((opt) => (
                        <button
                            key={`meeting-type-${opt.value}`}
                            type="button"
                            className={`meeting-choice-card ${meetingFormData.meetingType === opt.value ? 'is-active' : ''}`}
                            onClick={() => setMeetingFormData((prev) => ({ ...prev, meetingType: opt.value }))}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </Form.Group>
         
<Form.Group className="mb-3">
  <Form.Label>Toplantı Tipi (Şablon)</Form.Label>
  <div className="meeting-choice-grid meeting-choice-grid--compact">
    {MEETING_KIND_OPTIONS.map((opt) => (
      <button
        key={`meeting-kind-${opt.value}`}
        type="button"
        className={`meeting-choice-card ${meetingFormData.meetingKind === opt.value ? 'is-active' : ''}`}
        onClick={() => setMeetingFormData((prev) => ({ ...prev, meetingKind: opt.value }))}
      >
        {opt.label}
      </button>
    ))}
  </div>
</Form.Group>


            {showPhysicalFields && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Fiziksel Toplantı Yeri</Form.Label>
                        <Form.Control 
                            type="text" 
                            name="physicalLocation"
                            value={meetingFormData.physicalLocation}
                            onChange={handleMeetingChange}
                            placeholder="Toplantı adresini girin" 
                            required={meetingFormData.meetingType === '0' || meetingFormData.meetingType === '2'}
                        />
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>Fiziksel Toplantı Harita Konumu</Form.Label>
                        <Form.Control 
                            type="text" 
                            name="physicalMapLink"
                            value={meetingFormData.physicalMapLink}
                            onChange={handleMeetingChange}
                            placeholder="Harita bağlantısını girin (Google Maps vb.)" 
                        />
                    </Form.Group>
                </>
            )}

            {showOnlineFields && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Online Link Kaynağı</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                label="Manuel link"
                                name="meetingLinkSource"
                                value="manual"
                                checked={meetingLinkSource === 'manual'}
                                onChange={(e) => setMeetingLinkSource(e.target.value)}
                            />
                            {googleConnection.providerConnected && (
                                <Form.Check
                                    inline
                                    type="radio"
                                    label="Google Meet oluştur"
                                    name="meetingLinkSource"
                                    value="google"
                                    checked={meetingLinkSource === 'google'}
                                    onChange={(e) => setMeetingLinkSource(e.target.value)}
                                />
                            )}
                            {zoomConnection.providerConnected && (
                                <Form.Check
                                    inline
                                    type="radio"
                                    label="Zoom linki oluştur"
                                    name="meetingLinkSource"
                                    value="zoom"
                                    checked={meetingLinkSource === 'zoom'}
                                    onChange={(e) => setMeetingLinkSource(e.target.value)}
                                />
                            )}
                        </div>
                    </Form.Group>

                    {googleConnection.providerConnected && meetingLinkSource === 'google' && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                {isCheckingGoogle
                                    ? 'Google bağlantısı kontrol ediliyor...'
                                    : `Google bağlı: ${googleConnection.providerEmail || 'Hesap bağlı'}`}
                            </div>
                        </div>
                    )}

                    {zoomConnection.providerConnected && meetingLinkSource === 'zoom' && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                {isCheckingZoom
                                    ? 'Zoom bağlantısı kontrol ediliyor...'
                                    : `Zoom bağlı: ${zoomConnection.providerEmail || 'Hesap bağlı'}`}
                            </div>
                        </div>
                    )}

                    {!googleConnection.providerConnected && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                Google hesabı bağlı değil
                            </div>
                            <Button
                                variant="outline-primary"
                                type="button"
                                onClick={handleConnectGoogle}
                            >
                                Google hesabını bağla
                            </Button>
                        </div>
                    )}

                    {!zoomConnection.providerConnected && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                Zoom hesabı bağlı değil
                            </div>
                            <Button
                                variant="outline-primary"
                                type="button"
                                onClick={handleConnectZoom}
                            >
                                Zoom hesabını bağla
                            </Button>
                        </div>
                    )}

                    {meetingLinkSource === 'manual' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Online Toplantı Bağlantısı</Form.Label>
                            <Form.Control
                                type="text"
                                name="onlineMeetingLink"
                                value={meetingFormData.onlineMeetingLink}
                                onChange={handleMeetingChange}
                                placeholder="Zoom, Teams veya diğer toplantı bağlantısını girin"
                                required={meetingFormData.meetingType === '1' || meetingFormData.meetingType === '2'}
                            />
                        </Form.Group>
                    )}
                </>
            )}

            {submitError && <div className="alert alert-danger">{submitError}</div>}

            <Button 
                variant="primary" 
                type="submit" 
                className="mt-4 meeting-creator-submit"
                disabled={isLoading || isGeneratingMeetLink}
            >
                {isLoading || isGeneratingMeetLink
                    ? (isEditMode ? 'Güncelleniyor...' : 'Kaydediliyor...')
                    : (isEditMode ? 'Toplantı Güncelle' : 'Toplantı Kaydet')}
            </Button>
        </Form>
    );

    const renderEventForm = () => (
        <Form onSubmit={handleEventSubmit} className="meeting-creator-form">
            <Form.Group className="mb-3">
                <Form.Label>Etkinlik Başlığı</Form.Label>
                <Form.Control 
                    type="text" 
                    name="title"
                    value={eventFormData.title}
                    onChange={handleEventChange}
                    placeholder="Etkinlik başlığını girin" 
                    required
                />
            </Form.Group>

            <Row>
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>Başlangıç Tarihi</Form.Label>
                        <Form.Control 
                            type="date" 
                            name="startDate"
                            value={eventFormData.startDate}
                            onChange={handleEventChange}
                            required
                        />
                    </Form.Group>
                </Col>
                
                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>Başlangıç Saati</Form.Label>
                        <Form.Control 
                            type="time" 
                            name="startTime"
                            step={300}
                            value={eventFormData.startTime}
                            onChange={handleEventChange}
                            required
                        />
                    </Form.Group>
                </Col>

                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>Bitiş Tarihi</Form.Label>
                        <Form.Control 
                            type="date" 
                            name="endDate"
                            value={eventFormData.endDate}
                            onChange={handleEventChange}
                            required
                        />
                    </Form.Group>
                </Col>

                <Col md={3}>
                    <Form.Group className="mb-3">
                        <Form.Label>Bitiş Saati</Form.Label>
                        <Form.Control 
                            type="time" 
                            name="endTime"
                            step={300}
                            value={eventFormData.endTime}
                            onChange={handleEventChange}
                            required
                        />
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Açıklama</Form.Label>
                <Form.Control 
                    as="textarea" 
                    rows={3}
                    name="description"
                    value={eventFormData.description}
                    onChange={handleEventChange}
                    placeholder="Etkinliğin detayları, amacı veya diğer bilgileri..." 
                />
            </Form.Group>
            

            {/* Event Category - New from mobile */}
            <Form.Group className="mb-3">
                <Form.Label>Etkinlik Kategorisi</Form.Label>
                <Form.Select
                    name="eventCategory"
                    value={eventFormData.eventCategory}
                    onChange={handleEventChange}
                    required
                >
                    <option value="">Seçiniz</option>
                    <option value="expo">Expo</option>
                    <option value="congress">Congress</option>
                    <option value="conference">Conference</option>
                    <option value="trade_mission">Trade Mission</option>
                    <option value="meeting">Meeting</option>
                    <option value="peak">Peak</option>
                    <option value="workshop">Workshop</option>
                    <option value="symposium">Symposium</option>
                    <option value="seminar">Seminar</option>
                    <option value="conversation">Conversation</option>
                    <option value="launch">Launch</option>
                    <option value="award_ceremony">Award Ceremony</option>
                    <option value="celebration">Celebration</option>
                    <option value="career_days">Career Days</option>
                    <option value="education">Education</option>
                </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Etkinlik Türü</Form.Label>
                <Form.Select
                    name="eventScope"
                    value={eventFormData.eventScope}
                    onChange={handleEventChange}
                    required
                >
                    <option value="">Seçiniz</option>
                    <option value="kurumsal">Kurumsal</option>
                    <option value="bireysel">Bireysel</option>
                </Form.Select>
                

            </Form.Group>
{eventFormData.eventScope === 'kurumsal' && (
    <Form.Group className="mb-3">
        <Form.Label>Bağlı Kurum</Form.Label>

        {adminBusinesses.length > 0 ? (
            <Form.Select
                name="businessId"
                value={eventFormData.businessId}
                onChange={handleEventChange}
                required
            >
                <option value="">Kurum Seçiniz</option>
                {adminBusinesses.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                ))}
            </Form.Select>
        ) : (
            <div className="text-danger mt-2">
                Bağlı olduğunuz bir kurum bulunamadı.
            </div>
        )}
    </Form.Group>
)}

            <Form.Group className="mb-3">
                <Form.Label>Etkinlik Tipi</Form.Label>
                <Form.Select
                    name="eventType"
                    value={eventFormData.eventType}
                    onChange={handleEventChange}
                    required
                >
                    <option value="">Seçiniz</option>
                    <option value="fiziksel">Fiziksel</option>
                    <option value="online">Online</option>
                </Form.Select>
            </Form.Group>

            {eventFormData.eventType === 'fiziksel' && (
                <Form.Group className="mb-3">
                    <Form.Label>Harita Konumu</Form.Label>
                    <Form.Control 
                        type="text" 
                        name="mapLocation"
                        value={eventFormData.mapLocation}
                        onChange={handleEventChange}
                        placeholder="Google Maps bağlantısı"
                    />
                </Form.Group>
            )}

            {eventFormData.eventType === 'online' && (
                <>
                    <Form.Group className="mb-3">
                        <Form.Label>Online Link Kaynağı</Form.Label>
                        <div>
                            <Form.Check
                                inline
                                type="radio"
                                label="Manuel link"
                                name="eventLinkSource"
                                value="manual"
                                checked={eventLinkSource === 'manual'}
                                onChange={(e) => setEventLinkSource(e.target.value)}
                            />
                            {googleConnection.providerConnected && (
                                <Form.Check
                                    inline
                                    type="radio"
                                    label="Google Meet oluştur"
                                    name="eventLinkSource"
                                    value="google"
                                    checked={eventLinkSource === 'google'}
                                    onChange={(e) => setEventLinkSource(e.target.value)}
                                />
                            )}
                            {zoomConnection.providerConnected && (
                                <Form.Check
                                    inline
                                    type="radio"
                                    label="Zoom linki oluştur"
                                    name="eventLinkSource"
                                    value="zoom"
                                    checked={eventLinkSource === 'zoom'}
                                    onChange={(e) => setEventLinkSource(e.target.value)}
                                />
                            )}
                        </div>
                    </Form.Group>

                    {googleConnection.providerConnected && eventLinkSource === 'google' && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                {isCheckingGoogle
                                    ? 'Google bağlantısı kontrol ediliyor...'
                                    : `Google bağlı: ${googleConnection.providerEmail || 'Hesap bağlı'}`}
                            </div>
                        </div>
                    )}

                    {zoomConnection.providerConnected && eventLinkSource === 'zoom' && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                {isCheckingZoom
                                    ? 'Zoom bağlantısı kontrol ediliyor...'
                                    : `Zoom bağlı: ${zoomConnection.providerEmail || 'Hesap bağlı'}`}
                            </div>
                        </div>
                    )}

                    {!googleConnection.providerConnected && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                Google hesabı bağlı değil
                            </div>
                            <Button
                                variant="outline-primary"
                                type="button"
                                onClick={handleConnectGoogle}
                            >
                                Google hesabını bağla
                            </Button>
                        </div>
                    )}

                    {!zoomConnection.providerConnected && (
                        <div className="mb-3 p-3 border rounded">
                            <div className="mb-2">
                                Zoom hesabı bağlı değil
                            </div>
                            <Button
                                variant="outline-primary"
                                type="button"
                                onClick={handleConnectZoom}
                            >
                                Zoom hesabını bağla
                            </Button>
                        </div>
                    )}

                    {eventLinkSource === 'manual' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Online Etkinlik URL</Form.Label>
                            <Form.Control
                                type="text"
                                name="onlineUrl"
                                value={eventFormData.onlineUrl}
                                onChange={handleEventChange}
                                placeholder="Zoom, Meet bağlantısı"
                            />
                        </Form.Group>
                    )}
                </>
            )}

            <Form.Group className="mb-3">
                <Form.Label>Etkinlik Yeri</Form.Label>
                <Form.Control 
                    type="text" 
                    name="location"
                    value={eventFormData.location}
                    onChange={handleEventChange}
                    placeholder="Etkinlik adresi (görünecek yer adı)" 
                    required
                />
            </Form.Group>

            {/* New mobile features */}
            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Kontenjan</Form.Label>
                        <div className="meeting-choice-grid meeting-choice-grid--two">
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.quota === 'unlimited' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, quota: 'unlimited' })}
                            >
                                Sınırsız
                            </button>
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.quota === 'limited' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, quota: 'limited' })}
                            >
                                Sınırlı
                            </button>
                        </div>
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Web Sitesi?</Form.Label>
                        <div className="meeting-choice-grid meeting-choice-grid--two">
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.hasWebsite === 'yes' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, hasWebsite: 'yes' })}
                            >
                                Var
                            </button>
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.hasWebsite === 'no' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, hasWebsite: 'no' })}
                            >
                                Yok
                            </button>
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Katılımcı Onayı?</Form.Label>
                        <div className="meeting-choice-grid meeting-choice-grid--two">
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.participantApproval === 'yes' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, participantApproval: 'yes' })}
                            >
                                Evet
                            </button>
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.participantApproval === 'no' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, participantApproval: 'no' })}
                            >
                                Hayır
                            </button>
                        </div>
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Öne Çıkar</Form.Label>
                        <div className="meeting-choice-grid meeting-choice-grid--two">
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.suggestion === 'no' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, suggestion: 'no' })}
                            >
                                Hayır
                            </button>
                            <button
                                type="button"
                                className={`meeting-choice-card ${eventFormData.suggestion === 'yes' ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, suggestion: 'yes' })}
                            >
                                Evet
                            </button>
                        </div>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>Etkinlik ile İlgili Açıklama Ekleyin</Form.Label>
                <Form.Control 
                    as="textarea" 
                    rows={3}
                    name="addDescription"
                    value={eventFormData.addDescription}
                    onChange={handleEventChange}
                    placeholder="Etkinlik hakkında ek detaylar, özel bilgiler veya açıklamalar..." 
                />
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Kapak Resmi Ekle</Form.Label>
                <Form.Control 
                    type="file" 
                    accept="image/*"
                    onChange={handleCoverPhotoChange}
                    className="mb-2 meeting-file-input"
                />
                <Form.Text className="text-muted">
                    Etkinlik için kapak fotoğrafı seçin (opsiyonel)
                </Form.Text>
                {eventFormData.coverPhoto && (
                    <div className="mt-2">
                        <small className="text-success">
                            Seçilen dosya: {eventFormData.coverPhoto.name}
                        </small>
                    </div>
                )}
            </Form.Group>

            <Form.Group className="mb-3">
                <Form.Label>Döküman Ekle</Form.Label>
                <Form.Control 
                    type="file" 
                    name="documents"
                    multiple
                    onChange={handleEventChange}
                    className="mb-2 meeting-file-input"
                />
                <Form.Text className="text-muted">
                    Etkinlik ile ilgili dökümanları ekleyin (birden fazla dosya seçilebilir)
                </Form.Text>
                {eventFormData.documents.length > 0 && (
                    <div className="mt-2">
                        <strong>Seçilen Dosyalar:</strong>
                        <ul className="list-unstyled mt-1">
                            {eventFormData.documents.map((doc, index) => (
                                <li key={index} className="d-flex justify-content-between align-items-center">
                                    <small className="text-success">{doc.name}</small>
                                    <Button 
                                        variant="outline-danger" 
                                        size="sm"
                                        onClick={() => removeDocument(index)}
                                    >
                                        Kaldır
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </Form.Group>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>Katılım</Form.Label>
                        <div className="meeting-choice-grid meeting-choice-grid--two">
                            <button
                                type="button"
                                className={`meeting-choice-card ${(eventFormData.amount === '' || eventFormData.amount === '0') ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, amount: '0' })}
                            >
                                Ücretsiz
                            </button>
                            <button
                                type="button"
                                className={`meeting-choice-card ${(eventFormData.amount !== '' && eventFormData.amount !== '0') ? 'is-active' : ''}`}
                                onClick={() => setEventFormData({ ...eventFormData, amount: '' })}
                            >
                                Ücretli
                            </button>
                        </div>
                    </Form.Group>
                </Col>
                
                <Col md={6}>
                    {eventFormData.amount !== '0' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Etkinlik Katılım Ücreti</Form.Label>
                            <Form.Control 
                                type="number" 
                                name="amount"
                                value={eventFormData.amount}
                                onChange={handleEventChange}
                                placeholder="Ücret tutarı (TL)" 
                                min="0"
                                step="0.01"
                            />
                        </Form.Group>
                    )}
                </Col>
            </Row>

            {eventFormData.quota === 'limited' && (
                <Form.Group className="mb-3">
                    <Form.Label>Katılımcı Limiti</Form.Label>
                    <Form.Control 
                        type="number" 
                        name="capacity"
                        value={eventFormData.capacity}
                        onChange={handleEventChange}
                        placeholder="Maksimum katılımcı sayısı" 
                        min="1"
                        required
                    />
                </Form.Group>
            )}

            <Form.Group className="mb-3">
                <Form.Label>Etkinlik Ücreti Ödeme Bağlantısı</Form.Label>
                <Form.Control 
                    type="url" 
                    name="paymentLink"
                    value={eventFormData.paymentLink || ''}
                    onChange={handleEventChange}
                    placeholder="Ödeme sayfası URL'si (opsiyonel)" 
                />
                <Form.Text className="text-muted">
                    Katılımcıların ödeme yapacağı sayfanın bağlantısı
                </Form.Text>
            </Form.Group>

            <Button 
                variant="success" 
                type="submit" 
                className="mt-4 meeting-creator-submit"
                disabled={isLoading || isGeneratingMeetLink}
            >
                {isLoading || isGeneratingMeetLink ? 'Etkinlik Oluşturuluyor...' : 'Etkinliği Oluştur'}
            </Button>
        </Form>
    );

    return (
        <Container className="meeting-creator-page">
            <Card className="meeting-creator-card">
                <Card.Header className="meeting-creator-header">
                    <div className="meeting-creator-title-wrap">
                        <h4 className="meeting-creator-title">Planlama Asistanı</h4>
                        <p className="meeting-creator-subtitle">Toplantı veya iş etkinliğini hızlıca planlayın.</p>
                    </div>
                    <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab} className="meeting-creator-tabs">
                        <Nav.Item>
                            <Nav.Link eventKey="meeting">Toplantı Oluştur</Nav.Link>
                        </Nav.Item>
                        <Nav.Item>
                            <Nav.Link eventKey="event">İş Etkinliği Oluştur</Nav.Link>
                        </Nav.Item>
                    </Nav>
                </Card.Header>
                <Card.Body className="meeting-creator-body">
                    {activeTab === 'event' ? renderEventForm() : renderMeetingForm()}
                </Card.Body>
            </Card>
        </Container>
    );
}

export default MeetingEventCreator;
